/*
  GDriveFolderPickerDialog — folder-only picker for Document Import.

  Uses the same clinic-wide Drive connection as GDrivePickerDialog. The
  reviewer navigates the folder tree and clicks "Scan this folder" to
  hand a {id, name} back to the caller.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Loader2, Folder, ChevronRight, ChevronLeft, RefreshCw, Link2, Cloud, CheckCircle2, Search, Bug,
} from "lucide-react";

export default function GDriveFolderPickerDialog({ open, onOpenChange, onPick }) {
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [path, setPath] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scopeRoot, setScopeRoot] = useState(null); // {folder_id, folder_name}
  const [diagOpen, setDiagOpen] = useState(false);
  const [diag, setDiag] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(false);
  const pollerRef = useRef(null);
  const popupRef = useRef(null);

  const refreshStatus = useCallback(async () => {
    try {
      const r = await api.get("/exercise-programs/gdrive/status");
      setStatus(r.data || { connected: false });
      setScopeRoot(r.data?.documents_root || null);
      return r.data;
    } catch {
      setStatus({ connected: false });
    }
  }, []);

  const loadDiagnostics = useCallback(async () => {
    setLoadingDiag(true);
    try {
      const r = await api.get("/exercise-programs/gdrive/diagnostics", { params: { limit: 5 } });
      setDiag(r.data);
    } catch (e) {
      toast.error("Couldn't load diagnostics", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoadingDiag(false); }
  }, []);

  const loadFolder = useCallback(async (parentId) => {
    setLoading(true);
    try {
      const params = parentId
        ? { parent_id: parentId, scope: "documents" }
        : { scope: "documents" };
      const r = await api.get("/exercise-programs/gdrive/folders", { params });
      setFolders(r.data?.folders || []);
    } catch (e) {
      toast.error("Couldn't load Drive", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    refreshStatus().then((s) => {
      if (s?.connected) {
        // If a Documents root is configured, seed the breadcrumb with
        // it — this makes the "Scan this folder" button work at the
        // base of the scope (otherwise `currentFolder` is undefined).
        const root = s?.documents_root;
        setPath(root ? [{ id: root.folder_id, name: root.folder_name }] : []);
        loadFolder(root ? root.folder_id : null);
      }
    });
    async function onMessage(ev) {
      if (ev?.data?.type !== "gdrive_oauth") return;
      if (ev.data.error) {
        toast.error("Google Drive connect failed", {
          description: decodeURIComponent(ev.data.error).slice(0, 240),
        });
        setDiagOpen(true);
        loadDiagnostics();
        return;
      }
      if (ev.data.ok) {
        if (pollerRef.current) { clearInterval(pollerRef.current); pollerRef.current = null; }
        const s = await refreshStatus();
        if (s?.connected) {
          const root = s?.documents_root;
          setPath(root ? [{ id: root.folder_id, name: root.folder_name }] : []);
          await loadFolder(root ? root.folder_id : null);
          toast.success(
            s.account_email ? `Connected as ${s.account_email}` : "Google Drive connected",
          );
        }
      }
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      if (pollerRef.current) clearInterval(pollerRef.current);
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    };
  }, [open, refreshStatus, loadFolder]);

  async function startConnect() {
    setConnecting(true);
    const before = {
      email: status?.account_email || null,
      updatedAt: status?.updated_at || null,
    };
    try {
      const r = await api.get("/exercise-programs/gdrive/connect");
      const w = window.open(r.data.authorization_url, "gdrive-oauth", "width=520,height=640");
      popupRef.current = w;
      let popupClosedAt = 0;
      pollerRef.current = setInterval(async () => {
        const s = await refreshStatus();
        const changed =
          !!s?.connected &&
          (
            !before.updatedAt ||
            (s.updated_at && s.updated_at !== before.updatedAt) ||
            (s.account_email && s.account_email !== before.email)
          );
        if (changed) {
          clearInterval(pollerRef.current);
          if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
          const root = s?.documents_root;
          setPath(root ? [{ id: root.folder_id, name: root.folder_name }] : []);
          await loadFolder(root ? root.folder_id : null);
          toast.success(
            before.email && s.account_email && s.account_email !== before.email
              ? `Switched to ${s.account_email}`
              : s.account_email ? `Connected as ${s.account_email}` : "Google Drive connected",
          );
        } else if (popupRef.current?.closed) {
          if (!popupClosedAt) popupClosedAt = Date.now();
          if (Date.now() - popupClosedAt > 15000) {
            clearInterval(pollerRef.current);
          }
        }
      }, 1500);
    } catch (e) {
      toast.error("Couldn't start Drive connection", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setConnecting(false); }
  }

  function openFolder(f) {
    setPath((p) => [...p, { id: f.id, name: f.name }]);
    loadFolder(f.id);
  }
  function goBack() {
    setPath((p) => {
      // Don't allow popping past the scope root when one is set —
      // the root should always remain the first breadcrumb.
      const minLen = scopeRoot ? 1 : 0;
      if (p.length <= minLen) return p;
      const next = p.slice(0, -1);
      loadFolder(next[next.length - 1]?.id || null);
      return next;
    });
  }

  const currentFolder = path[path.length - 1];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[92vw] max-h-[85vh] flex flex-col"
                     data-testid="gdrive-folder-picker">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="h-4 w-4 text-indigo-700" />
            Pick a Google Drive folder
            {status?.connected && (
              <span className="ml-1 text-2xs font-normal text-emerald-700 inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> connected
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            The scanner will pick up all .pdf, .docx, .jpg and .png files
            directly inside the folder you choose.
          </DialogDescription>
          {status?.connected && (
            <div className="mt-1 flex items-center gap-2 text-2xs text-zinc-600"
                 data-testid="gdrive-folder-connected-account">
              <span>Connected as</span>
              <span className="font-medium text-zinc-800 truncate max-w-[240px]"
                    title={status.account_email || "unknown account"}>
                {status.account_email || <span className="italic text-zinc-500">unknown account</span>}
              </span>
              <span className="text-zinc-300">·</span>
              <button
                onClick={startConnect}
                disabled={connecting}
                className="text-indigo-700 hover:underline inline-flex items-center gap-1"
                data-testid="gdrive-folder-switch-account-btn"
                title="Sign in with a different Google account"
              >
                {connecting
                  ? <><Loader2 className="h-3 w-3 animate-spin" /> Opening…</>
                  : <>Switch account</>}
              </button>
            </div>
          )}
        </DialogHeader>

        {!status?.connected ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 py-10 text-center">
            <Cloud className="h-8 w-8 text-zinc-400" />
            <div className="text-sm text-zinc-700 max-w-sm">
              Google Drive isn&apos;t connected. Connect the clinic&apos;s Drive to
              start bulk imports.
            </div>
            <Button onClick={startConnect} disabled={connecting} data-testid="folder-picker-connect">
              {connecting
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Opening Google…</>
                : <><Link2 className="h-3.5 w-3.5 mr-1" /> Connect Google Drive</>}
            </Button>
            <button
              type="button"
              onClick={() => { setDiagOpen((v) => !v); if (!diag) loadDiagnostics(); }}
              className="text-2xs text-zinc-500 hover:text-zinc-700 underline inline-flex items-center gap-1 mt-2"
              data-testid="gdrive-folder-diagnostics-toggle"
            >
              <Bug className="h-3 w-3" />
              {diagOpen ? "Hide diagnostics" : "View last connection attempt"}
            </button>
            {diagOpen && (
              <div className="w-full max-w-xl text-left border border-zinc-200 rounded-md p-3 bg-zinc-50 text-2xs font-mono text-zinc-700 overflow-x-auto"
                   data-testid="gdrive-folder-diagnostics-panel">
                {loadingDiag ? (
                  <div className="text-zinc-500">Loading…</div>
                ) : !diag ? (
                  <div className="text-zinc-500">No data yet.</div>
                ) : (
                  <>
                    <div className="mb-2 pb-2 border-b border-zinc-200">
                      <div className="font-semibold text-zinc-800 mb-1">OAuth config</div>
                      <div>redirect_uri: <span className="text-indigo-700 break-all">{diag.config?.redirect_uri}</span></div>
                      <div>client_id: …{diag.config?.client_id_tail}</div>
                      <div>requested_scopes: {(diag.config?.requested_scopes || []).join(", ") || "—"}</div>
                      <div>acceptable_scopes: {(diag.config?.acceptable_scopes || []).join(", ") || "—"}</div>
                    </div>
                    <div className="font-semibold text-zinc-800 mb-1">
                      Recent attempts ({diag.attempts?.length || 0})
                    </div>
                    {(diag.attempts || []).length === 0 && (
                      <div className="text-zinc-500">No attempts recorded yet.</div>
                    )}
                    {(diag.attempts || []).map((a, i) => (
                      <div key={i} className="mb-2 pb-2 border-b border-zinc-200 last:border-b-0 last:mb-0 last:pb-0">
                        <div className={a.status === "success" ? "text-emerald-700" : "text-red-700"}>
                          [{a.at}] {a.status}{a.stage ? ` @ ${a.stage}` : ""}
                        </div>
                        {a.error && <div>error: {a.error}</div>}
                        {a.error_detail && <div className="whitespace-pre-wrap break-all">detail: {a.error_detail}</div>}
                        {a.requested_scopes && <div>requested: {a.requested_scopes.join(", ")}</div>}
                        {a.granted_scopes && <div>granted: {a.granted_scopes.join(", ") || "none"}</div>}
                        {a.raw_token_scope_field !== undefined && a.raw_token_scope_field !== null && (
                          <div className="text-amber-800">raw scope field: {a.raw_token_scope_field || <em>empty string</em>}</div>
                        )}
                        {a.raw_token_fields && (
                          <div>raw token fields: {a.raw_token_fields.join(", ")}</div>
                        )}
                        {a.google_scope_param && <div>google_scope_param: {a.google_scope_param}</div>}
                        {a.authuser !== undefined && a.authuser !== null && <div>authuser: {a.authuser}</div>}
                        {a.prompt_param && <div>prompt_param: {a.prompt_param}</div>}
                        {a.account_email && <div>account_email: {a.account_email}</div>}
                        {a.has_refresh_token !== undefined && (
                          <div>refresh_token: {a.has_refresh_token ? "yes" : "no"} · id_token: {a.has_id_token ? "yes" : "no"}</div>
                        )}
                      </div>
                    ))}
                  </>
                )}
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="ghost" onClick={loadDiagnostics} disabled={loadingDiag}
                          data-testid="gdrive-folder-diagnostics-refresh">
                    <RefreshCw className="h-3 w-3 mr-1" /> Refresh
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {scopeRoot && (
              <div
                className="flex items-center gap-2 px-2 py-1.5 mt-2 text-2xs rounded bg-indigo-50 border border-indigo-100 text-indigo-800"
                data-testid="gdrive-folder-scope-banner"
              >
                <Cloud className="h-3 w-3" />
                <span>
                  Imports scoped to <span className="font-medium">{scopeRoot.folder_name}</span>
                  {" "}— folders outside this are hidden.
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 py-2 border-b border-zinc-200 text-xs text-zinc-600">
              <Button
                size="sm" variant="ghost" onClick={goBack}
                disabled={path.length <= (scopeRoot ? 1 : 0)}
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Back
              </Button>
              <span className="flex items-center gap-1 flex-1 min-w-0 truncate">
                {!scopeRoot && <span className="text-zinc-500">My Drive</span>}
                {path.map((p, idx) => (
                  <span key={p.id} className="flex items-center gap-1">
                    {(idx > 0 || !scopeRoot) && (
                      <ChevronRight className="h-3 w-3 text-zinc-400" />
                    )}
                    <span className="truncate">{p.name}</span>
                  </span>
                ))}
              </span>
              <Button size="sm" variant="ghost" onClick={() => loadFolder(currentFolder?.id || null)}>
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto py-2 space-y-1">
              {loading ? (
                <div className="text-sm text-zinc-500 py-6 text-center">
                  <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
                </div>
              ) : folders.length === 0 ? (
                <div className="text-sm text-zinc-500 py-6 text-center">
                  No sub-folders here. Click &quot;Scan this folder&quot; below to
                  scan the current location.
                </div>
              ) : (
                folders.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => openFolder(f)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-100 text-left text-sm"
                    data-testid={`folder-${f.id}`}
                  >
                    <Folder className="h-4 w-4 text-amber-600" />
                    <span className="flex-1 truncate">{f.name}</span>
                    <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
                  </button>
                ))
              )}
            </div>
          </>
        )}

        <DialogFooter className="border-t pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => currentFolder && onPick(currentFolder)}
            disabled={!currentFolder || !status?.connected}
            data-testid="scan-this-folder-btn"
          >
            <Search className="h-3.5 w-3.5 mr-1" />
            Scan this folder{currentFolder ? ` — ${currentFolder.name}` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
